/**
 * Losi Business Seats
 *
 * Provides seat-capacity helpers and assignment management for the
 * Losi Pro seats included in business subscriptions.
 *
 * Primary responsibilities:
 * - Resolve Business owner and teammate seat access
 * - Manage explicit teammate seat assignments
 * - List assignable workspace members for seat management
 *
 * Dependencies:
 * - Supabase service client
 * - Nexus subscription billing helpers
 * - Shared Losi pricing config
 */

import { Tables } from '@/supabase/types'
import {
  getBusinessPlanByInternalNexusPlan,
  getPersonalPlanRank,
  normalizePersonalPlanTier
} from '@/lib/billing/losi-pricing'
import { createServiceClient } from '@/lib/supabase/service-client'
import { getNexusSubscription } from '@/db/nexus-billing'

export interface LosiBusinessSeatRow {
  id: string
  owner_user_id: string
  assigned_user_id: string
  assigned_by_user_id: string
  workspace_id: string | null
  losi_plan: 'pro'
  created_at: string
  updated_at: string
}

export interface LosiBusinessSeatEntitlement {
  plan: 'pro'
  ownerUserId: string
  workspaceId: string | null
  source: 'owner_subscription' | 'assigned_seat'
}

export interface LosiBusinessSeatSummary {
  planName: string
  totalIncludedSeats: number
  ownerIncludedSeat: boolean
  assignedTeamSeats: number
  availableTeamSeats: number
}

export interface AssignableSeatMember {
  userId: string
  workspaceId: string
  workspaceName: string
  role: string
  displayName: string
  username: string | null
  imageUrl: string | null
  isAssigned: boolean
}

type UntypedClient = {
  from: (table: string) => any
}

const getSeatClient = (): UntypedClient => {
  if (typeof window === 'undefined') {
    return createServiceClient() as unknown as UntypedClient
  }

  return require('@/lib/supabase/browser-client').supabase as UntypedClient
}

async function getOwnerSeatCapacity(ownerUserId: string): Promise<{
  summary: LosiBusinessSeatSummary
  subscriptionId: string
} | null> {
  const subscription = await getNexusSubscription(ownerUserId)
  const businessPlan = getBusinessPlanByInternalNexusPlan(subscription?.plan_type ?? null)

  if (!subscription || !businessPlan) {
    return null
  }

  const assignedSeats = await listRawAssignedSeats(ownerUserId)
  const teamSeatCapacity = Math.max(0, businessPlan.includedProSeats - 1)
  const assignedTeamSeats = assignedSeats.length

  return {
    subscriptionId: subscription.id,
    summary: {
      planName: businessPlan.publicName,
      totalIncludedSeats: businessPlan.includedProSeats,
      ownerIncludedSeat: true,
      assignedTeamSeats,
      availableTeamSeats: Math.max(0, teamSeatCapacity - assignedTeamSeats)
    }
  }
}

async function listRawAssignedSeats(ownerUserId: string): Promise<LosiBusinessSeatRow[]> {
  const { data, error } = await getSeatClient()
    .from('losi_business_seats')
    .select('*')
    .eq('owner_user_id', ownerUserId)
    .order('created_at', { ascending: true })

  if (error) {
    throw new Error(error.message || 'Failed to list Losi Business seats')
  }

  return (data ?? []) as LosiBusinessSeatRow[]
}

export async function getBusinessSeatSummary(ownerUserId: string) {
  const capacity = await getOwnerSeatCapacity(ownerUserId)
  return capacity?.summary ?? null
}

export async function getBusinessSeatEntitlement(
  userId: string
): Promise<LosiBusinessSeatEntitlement | null> {
  const ownSubscription = await getNexusSubscription(userId)
  const ownBusinessPlan = getBusinessPlanByInternalNexusPlan(ownSubscription?.plan_type ?? null)

  if (ownSubscription && ownBusinessPlan) {
    return {
      plan: 'pro',
      ownerUserId: userId,
      workspaceId: null,
      source: 'owner_subscription'
    }
  }

  const { data, error } = await getSeatClient()
    .from('losi_business_seats')
    .select('*')
    .eq('assigned_user_id', userId)
    .maybeSingle()

  if (error) {
    throw new Error(error.message || 'Failed to resolve Losi Business seat')
  }

  const seat = data as LosiBusinessSeatRow | null
  if (!seat) {
    return null
  }

  const ownerCapacity = await getOwnerSeatCapacity(seat.owner_user_id)
  if (!ownerCapacity) {
    return null
  }

  return {
    plan: 'pro',
    ownerUserId: seat.owner_user_id,
    workspaceId: seat.workspace_id,
    source: 'assigned_seat'
  }
}

export async function applyBusinessSeatEntitlement(
  profile: Tables<'profiles'>
): Promise<Tables<'profiles'>> {
  const entitlement = await getBusinessSeatEntitlement(profile.user_id)
  if (!entitlement) {
    return profile
  }

  const currentPlan = normalizePersonalPlanTier(profile.plan)
  if (getPersonalPlanRank(currentPlan) >= getPersonalPlanRank('pro')) {
    return profile
  }

  return {
    ...profile,
    plan: 'pro'
  }
}

export async function listBusinessSeatAssignments(ownerUserId: string) {
  const summary = await getBusinessSeatSummary(ownerUserId)
  if (!summary) {
    return {
      summary: null,
      seats: [] as Array<
        | {
            kind: 'owner'
            userId: string
            workspaceId: null
            assignedAt: null
            losiPlan: 'pro'
          }
        | {
            kind: 'member'
            userId: string
            workspaceId: string | null
            assignedAt: string
            losiPlan: 'pro'
          }
      >
    }
  }

  const assignedSeats = await listRawAssignedSeats(ownerUserId)

  return {
    summary,
    seats: [
      {
        kind: 'owner' as const,
        userId: ownerUserId,
        workspaceId: null,
        assignedAt: null,
        losiPlan: 'pro' as const
      },
      ...assignedSeats.map((seat) => ({
        kind: 'member' as const,
        userId: seat.assigned_user_id,
        workspaceId: seat.workspace_id,
        assignedAt: seat.created_at,
        losiPlan: seat.losi_plan
      }))
    ]
  }
}

export async function listAssignableBusinessSeatMembers(
  ownerUserId: string
): Promise<AssignableSeatMember[]> {
  const client = getSeatClient()
  const { data: ownedWorkspaces, error: workspaceError } = await client
    .from('workspaces')
    .select('id, name')
    .eq('user_id', ownerUserId)

  if (workspaceError) {
    throw new Error(workspaceError.message || 'Failed to load owned workspaces')
  }

  if (!ownedWorkspaces?.length) {
    return []
  }

  const workspaceIds = ownedWorkspaces.map((workspace) => workspace.id)
  const workspaceNames = new Map(
    ownedWorkspaces.map((workspace) => [workspace.id, workspace.name])
  )

  const { data: memberships, error: membershipError } = await client
    .from('workspace_members')
    .select(
      `
        workspace_id,
        user_id,
        role,
        profiles!workspace_members_user_profile_fkey(
          user_id,
          username,
          display_name,
          image_url
        )
      `
    )
    .in('workspace_id', workspaceIds)

  if (membershipError) {
    throw new Error(membershipError.message || 'Failed to load workspace members')
  }

  const assignedSeats = await listRawAssignedSeats(ownerUserId)
  const assignedUserIds = new Set(assignedSeats.map((seat) => seat.assigned_user_id))
  const membersByUserId = new Map<string, AssignableSeatMember>()

  for (const membership of memberships ?? []) {
    const typedMembership = membership as {
      workspace_id: string
      user_id: string
      role: string
      profiles:
        | {
            user_id: string
            username: string | null
            display_name: string | null
            image_url: string | null
          }
        | Array<{
            user_id: string
            username: string | null
            display_name: string | null
            image_url: string | null
          }>
        | null
    }

    if (typedMembership.user_id === ownerUserId) {
      continue
    }

    const rawProfile = Array.isArray(typedMembership.profiles)
      ? typedMembership.profiles[0]
      : typedMembership.profiles

    const current = membersByUserId.get(typedMembership.user_id)
    const nextMember: AssignableSeatMember = {
      userId: typedMembership.user_id,
      workspaceId: typedMembership.workspace_id,
      workspaceName: workspaceNames.get(typedMembership.workspace_id) ?? 'Workspace',
      role: typedMembership.role,
      displayName:
        rawProfile?.display_name || rawProfile?.username || typedMembership.user_id,
      username: rawProfile?.username ?? null,
      imageUrl: rawProfile?.image_url ?? null,
      isAssigned: assignedUserIds.has(typedMembership.user_id)
    }

    if (!current) {
      membersByUserId.set(typedMembership.user_id, nextMember)
      continue
    }

    if (current.isAssigned) {
      continue
    }

    membersByUserId.set(typedMembership.user_id, nextMember)
  }

  return [...membersByUserId.values()].sort((left, right) =>
    left.displayName.localeCompare(right.displayName)
  )
}

export async function assignBusinessSeat(params: {
  ownerUserId: string
  assignedUserId: string
  assignedByUserId: string
  workspaceId?: string | null
}) {
  const { ownerUserId, assignedUserId, assignedByUserId, workspaceId = null } = params
  const capacity = await getOwnerSeatCapacity(ownerUserId)

  if (!capacity) {
    throw new Error('An active Losi Business subscription is required')
  }

  if (ownerUserId === assignedUserId) {
    throw new Error('The plan owner already receives the included Pro seat')
  }

  if (capacity.summary.availableTeamSeats <= 0) {
    throw new Error('No additional Losi Pro seats are available on this plan')
  }

  const { data: existingSeat } = await getSeatClient()
    .from('losi_business_seats')
    .select('*')
    .eq('assigned_user_id', assignedUserId)
    .maybeSingle()

  const typedExistingSeat = existingSeat as LosiBusinessSeatRow | null
  if (typedExistingSeat && typedExistingSeat.owner_user_id !== ownerUserId) {
    throw new Error('This user already has a Losi Business seat from another owner')
  }

  const { data, error } = await getSeatClient()
    .from('losi_business_seats')
    .upsert(
      {
        owner_user_id: ownerUserId,
        assigned_user_id: assignedUserId,
        assigned_by_user_id: assignedByUserId,
        workspace_id: workspaceId,
        losi_plan: 'pro',
        updated_at: new Date().toISOString()
      },
      {
        onConflict: 'assigned_user_id'
      }
    )
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message || 'Failed to assign Losi Business seat')
  }

  return data as LosiBusinessSeatRow
}

export async function removeBusinessSeat(ownerUserId: string, assignedUserId: string) {
  const { error } = await getSeatClient()
    .from('losi_business_seats')
    .delete()
    .eq('owner_user_id', ownerUserId)
    .eq('assigned_user_id', assignedUserId)

  if (error) {
    throw new Error(error.message || 'Failed to remove Losi Business seat')
  }

  return true
}
